import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import { useNewChat } from '@/api/hooks/useChat'
import { useUserStore } from '@/stores/userStore'
import { useChatStore } from '@/stores/chatStore'
import { useSessionCache } from '@/hooks/useSessionCache'
import type { AgentRes } from '@/api/types/agent.type'
import type { AgentSearchConfig } from '@/api/hooks/useAgent'
import '@/styles/scrollbar.css'

interface Message {
  id: string
  content: string
  isUser: boolean
  timestamp: Date
  avatar?: string
}

interface ChatSessionProps {
  sessionId: string
  isActive: boolean
  initialMessage?: string
  agentId?: string // 智能体ID参数
  agentDetail?: AgentRes // 新增智能体详情参数
  searchConfig?: AgentSearchConfig | null // 新增搜索配置参数
  onSendMessage?: (sessionId: string, content: string) => Promise<void>
}

const ChatSession: React.FC<ChatSessionProps> = ({
  sessionId,
  isActive,
  initialMessage,
  agentId,
  agentDetail,
}) => {
  const { currentUser } = useUserStore()
  const { getMessages, addMessage } = useChatStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const sessionCache = useSessionCache()

  // 本地消息状态 - 使用Map来管理每个会话的本地消息
  const [localMessagesMap, setLocalMessagesMap] = useState<
    Map<string, Message[]>
  >(new Map())
  const [isSending, setIsSending] = useState(false)
  const [hasProcessedInitialMessage, setHasProcessedInitialMessage] =
    useState(false)
  const [_, setCurrentAiMessageId] = useState<string | null>(null)

  // 获取当前会话的本地消息
  const localMessages = useMemo(() => {
    return localMessagesMap.get(sessionId) || []
  }, [localMessagesMap, sessionId])

  // 更新当前会话的本地消息
  const updateLocalMessages = useCallback(
    (newMessages: Message[] | ((prev: Message[]) => Message[])) => {
      setLocalMessagesMap((prev) => {
        const newMap = new Map(prev)
        const currentMessages = newMap.get(sessionId) || []
        const updatedMessages =
          typeof newMessages === 'function'
            ? newMessages(currentMessages)
            : newMessages
        newMap.set(sessionId, updatedMessages)
        return newMap
      })
    },
    [sessionId]
  )

  const userAvatar = useMemo(
    () =>
      currentUser?.avatar ||
      'https://api.dicebear.com/7.x/avataaars/svg?seed=User',
    [currentUser?.avatar]
  )
  const aiAvatar = useMemo(
    () =>
      agentDetail?.icon || 'https://api.dicebear.com/7.x/avataaars/svg?seed=AI',
    [agentDetail?.icon]
  )

  // 稳定化缓存检查，避免无限重新渲染
  const isCached = useMemo(() => {
    return sessionCache.isSessionCached(sessionId)
  }, [sessionId, sessionCache.cacheSize])

  // 获取会话消息 - 暂时使用空数组，因为不再调用API
  // const apiMessages: any[] = []
  const messagesLoading = false

  // 转换API消息格式
  // const convertedMessages = useMemo(() => {
  //   // 优先使用缓存数据
  //   const cachedMessages = sessionCache.getCachedSession(sessionId)
  //   const messagesToUse = cachedMessages || apiMessages

  //   if (messagesToUse.length > 0) {
  //     const converted = messagesToUse.map((msg) => ({
  //       id: msg.id.toString(),
  //       content: msg.content,
  //       isUser: msg.role === 'user',
  //       timestamp: new Date(msg.createdAt),
  //       avatar: msg.role === 'user' ? userAvatar : aiAvatar,
  //     }))

  //     return converted
  //   }

  //   return []
  // }, [apiMessages, sessionId, sessionCache.cacheSize, userAvatar, aiAvatar])

  // 简化：直接使用本地消息
  const messages = useMemo(() => {
    console.log(
      '当前本地消息:',
      localMessages.map((m) => ({ id: m.id, content: m.content }))
    )
    return localMessages.sort((a, b) => {
      // 确保 timestamp 是 Date 对象
      const timestampA =
        a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp)
      const timestampB =
        b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp)
      return timestampA.getTime() - timestampB.getTime()
    })
  }, [localMessages])

  // 处理SSE消息的回调函数 - 简化版本
  const handleSSEMessage = useCallback(
    (event: any) => {
      console.log('ChatSession收到SSE消息:', event)

      try {
        const data = JSON.parse(event.data)
        console.log('解析的SSE数据:', data)

        // 直接更新最新的AI消息内容
        updateLocalMessages((prev) => {
          console.log(
            '更新前的本地消息:',
            prev.map((m) => ({ id: m.id, content: m.content }))
          )

          // 找到最新的AI消息（非用户消息）
          const aiMessages = prev.filter((msg) => !msg.isUser)
          const latestAiMessage = aiMessages[aiMessages.length - 1]

          if (latestAiMessage) {
            console.log('找到最新的AI消息:', latestAiMessage.id)
            const updated = prev.map((msg) =>
              msg.id === latestAiMessage.id
                ? {
                    ...msg,
                    content:
                      msg.content === '正在思考...'
                        ? data.msg || ''
                        : msg.content + (data.msg || ''),
                  }
                : msg
            )
            console.log(
              '更新后的本地消息:',
              updated.map((m) => ({ id: m.id, content: m.content }))
            )

            return updated
          } else {
            console.log('没有找到AI消息')
            return prev
          }
        })
      } catch (error) {
        console.error('解析SSE消息失败:', error)
      }
    },
    [updateLocalMessages]
  )

  // 处理SSE错误的回调函数
  const handleSSEError = useCallback((error: Event) => {
    console.error('ChatSession SSE错误:', error)
    setIsSending(false)
  }, [])

  // 处理SSE完成的回调函数
  const handleSSEDone = useCallback(() => {
    setIsSending(false)
    setCurrentAiMessageId(null)

    // 在SSE接收完所有消息后，将完整的AI消息存储到store中
    setLocalMessagesMap((currentMap) => {
      const currentMessages = currentMap.get(sessionId) || []
      if (currentMessages.length > 0) {
        // 获取store中已有的消息
        const existingMessages = getMessages(sessionId)
        const existingMessageIds = new Set(
          existingMessages.map((msg) => msg.id)
        )

        // 找到最新的AI消息（非用户消息且不是占位符）
        const aiMessages = currentMessages.filter((msg) => !msg.isUser)
        const latestAiMessage = aiMessages[aiMessages.length - 1]

        if (latestAiMessage && latestAiMessage.content !== '正在思考...') {
          // 检查AI消息是否已存在
          if (!existingMessageIds.has(latestAiMessage.id)) {
            console.log('存储完整的AI消息到store:', latestAiMessage.content)
            addMessage(sessionId, {
              ...latestAiMessage,
              avatar: latestAiMessage.avatar || aiAvatar,
            })
          }
        }
      }
      return currentMap
    })
  }, [sessionId, addMessage, aiAvatar, getMessages])

  // 创建新的聊天Hook实例，传入回调函数
  // 从sessionStorage中获取searchConfig
  const storedSearchConfig = useMemo(() => {
    try {
      const storedConfig = sessionStorage.getItem(`search_config_${sessionId}`)
      return storedConfig ? JSON.parse(storedConfig) : null
    } catch (error) {
      console.error('解析searchConfig失败:', error)
      return null
    }
  }, [sessionId])

  const { sendMessage: sendNewMessage } = useNewChat(
    sessionId,
    agentId,
    agentDetail?.setting,
    storedSearchConfig,
    {
      onMessage: handleSSEMessage,
      onError: handleSSEError,
      onDone: handleSSEDone,
    }
  )

  // 从store加载历史消息
  useEffect(() => {
    if (isActive && localMessages.length === 0 && !initialMessage) {
      const storedMessages = getMessages(sessionId)
      if (storedMessages.length > 0) {
        // 确保 timestamp 是 Date 对象
        const messagesWithDateTimestamps = storedMessages.map((message) => ({
          ...message,
          timestamp:
            message.timestamp instanceof Date
              ? message.timestamp
              : new Date(message.timestamp),
        }))
        updateLocalMessages(messagesWithDateTimestamps)
      }
    }
  }, [
    isActive,
    sessionId,
    localMessages.length,
    initialMessage,
    getMessages,
    updateLocalMessages,
  ])

  // 处理初始消息
  useEffect(() => {
    if (
      initialMessage &&
      isActive &&
      localMessages.length === 0 &&
      !hasProcessedInitialMessage &&
      !isCached
    ) {
      console.log(`ChatSession ${sessionId}: 处理初始消息`, {
        initialMessage,
        isActive,
        localMessagesLength: localMessages.length,
        hasProcessedInitialMessage,
        isCached,
      })

      setHasProcessedInitialMessage(true)
      setIsSending(true)

      // 添加用户消息
      const userMessage: Message = {
        id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: initialMessage,
        isUser: true,
        timestamp: new Date(),
        avatar: userAvatar,
      }
      console.log(`ChatSession ${sessionId}: 添加初始用户消息到本地消息`)
      updateLocalMessages((prev) => [...prev, userMessage])

      // 立即存储用户消息到store
      addMessage(sessionId, {
        ...userMessage,
        avatar: userMessage.avatar || userAvatar,
      })

      // 创建AI消息占位符
      const aiMessageId = `ai_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`
      const aiMessage: Message = {
        id: aiMessageId,
        content: '正在思考...',
        isUser: false,
        timestamp: new Date(),
        avatar: aiAvatar,
      }
      console.log(`ChatSession ${sessionId}: 添加初始AI消息占位符`)
      updateLocalMessages((prev) => [...prev, aiMessage])

      // 不存储占位符消息到store，只在本地显示

      setCurrentAiMessageId(aiMessageId)
      console.log(`设置currentAiMessageId为: ${aiMessageId}`)

      const currentSessionId = sessionId

      // 异步发送消息到AI
      console.log(`ChatSession ${currentSessionId}: 调用新API发送初始消息`)
      sendNewMessage(initialMessage)
        .then(() => {
          console.log(`ChatSession ${currentSessionId}: 初始消息发送成功`)
        })
        .catch((error) => {
          console.error('发送初始消息失败:', error)

          // 检查会话是否还是发送消息时的会话
          if (sessionId !== currentSessionId) {
            console.log('会话已切换，忽略初始消息错误处理')
            return
          }

          // 更新AI消息为错误状态
          updateLocalMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, content: '抱歉，发送消息时出现错误，请稍后重试。' }
                : msg
            )
          )

          // 存储错误消息到store
          addMessage(sessionId, {
            id: aiMessageId,
            content: '抱歉，发送消息时出现错误，请稍后重试。',
            isUser: false,
            timestamp: new Date(),
            avatar: aiAvatar,
          })

          setIsSending(false)
          setCurrentAiMessageId(null)
        })
    } else {
      console.log('Initial message conditions not met:', {
        hasInitialMessage: !!initialMessage,
        isActive,
        localMessagesLength: localMessages.length,
        hasProcessedInitialMessage,
        isCached,
      })
    }
  }, [
    initialMessage,
    isActive,
    sessionId,
    hasProcessedInitialMessage,
    isCached,
    localMessages,
    updateLocalMessages,
    userAvatar,
    aiAvatar,
    sendNewMessage,
  ])

  // 自动滚动到底部
  useEffect(() => {
    if (isActive && messages.length > 0) {
      const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
      scrollToBottom()
    }
  }, [messages, isActive])

  // 发送消息处理函数
  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isSending) return

    console.log(`ChatSession ${sessionId}: 发送消息`, { content })

    setIsSending(true)

    // 添加用户消息
    const userMessage: Message = {
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content: content,
      isUser: true,
      timestamp: new Date(),
      avatar: userAvatar,
    }
    console.log(`ChatSession ${sessionId}: 添加用户消息到本地消息`)
    updateLocalMessages((prev) => [...prev, userMessage])

    // 立即存储用户消息到store
    addMessage(sessionId, {
      ...userMessage,
      avatar: userMessage.avatar || userAvatar,
    })

    // 创建AI消息占位符
    const aiMessageId = `ai_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`
    const aiMessage: Message = {
      id: aiMessageId,
      content: '正在思考...',
      isUser: false,
      timestamp: new Date(),
      avatar: aiAvatar,
    }
    console.log(`ChatSession ${sessionId}: 添加AI消息占位符`)
    updateLocalMessages((prev) => [...prev, aiMessage])

    // 不存储占位符消息到store，只在本地显示

    setCurrentAiMessageId(aiMessageId)
    console.log(`设置currentAiMessageId为: ${aiMessageId}`)

    const currentSessionId = sessionId

    try {
      console.log(`ChatSession ${currentSessionId}: 调用新API发送消息`)
      await sendNewMessage(content)
      console.log(`ChatSession ${currentSessionId}: API调用完成`)
    } catch (error) {
      console.error('发送消息失败:', error)

      // 检查会话是否还是发送消息时的会话
      if (sessionId !== currentSessionId) {
        console.log('会话已切换，忽略错误处理')
        return
      }

      // 更新AI消息为错误状态
      updateLocalMessages((prev) =>
        prev.map((msg) =>
          msg.id === aiMessageId
            ? { ...msg, content: '抱歉，发送消息时出现错误，请稍后重试。' }
            : msg
        )
      )

      // 存储错误消息到store
      addMessage(sessionId, {
        id: aiMessageId,
        content: '抱歉，发送消息时出现错误，请稍后重试。',
        isUser: false,
        timestamp: new Date(),
        avatar: aiAvatar,
      })

      setIsSending(false)
      setCurrentAiMessageId(null)
    }
  }

  // 如果会话不活跃，不渲染内容
  if (!isActive) {
    return null
  }

  // 如果正在加载且没有消息，显示加载状态
  if (messagesLoading && messages.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm">正在加载对话...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {messages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <p className="text-gray-500 text-sm">开始新的对话</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                content={message.content}
                isUser={message.isUser}
                timestamp={message.timestamp}
                avatar={message.avatar}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 输入区域 */}
      <div className="border-t border-gray-200 p-4">
        <ChatInput onSendMessage={handleSendMessage} disabled={isSending} />
      </div>
    </div>
  )
}

export default ChatSession
